import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  BodyText,
  LabelText,
  Checkbox,
  TextArea,
} from '@spotoninc/nexus-react';
import { format } from 'date-fns';

// Survey reasons (PROF-1191).
export const CANCEL_REASONS = [
  { value: 'not_enough_use', label: "I don't use it enough" },
  { value: 'using_another_platform', label: "I'm using another platform" },
  { value: 'too_expensive', label: "It's too expensive for me right now" },
  { value: 'other', label: 'Other' },
];

// Subheading above the survey reasons (Figma node 20393:11610).
const SURVEY_PROMPT = "Can you tell us why you're leaving?";

// The "Other" reason requires the free-text field to be filled in.
const OTHER_REASON = 'other';
const DETAIL_REQUIRED_MESSAGE = 'Please tell us why so we can improve.';

// Maps a survey reason `value` code to the verbatim label string the BFF stores
// (PROF-1193 sends `reasons` as an array of these exact labels, no mapping layer
// on the BE). Built once from CANCEL_REASONS so the two never drift.
const LABEL_BY_VALUE = CANCEL_REASONS.reduce((acc, { value, label }) => {
  acc[value] = label;
  return acc;
}, {});

// Per-category error copy for a rejected cancellation (see CancellationError):
//  - unauthenticated (401): session expired — prompt a re-login.
//  - not_found (404): nothing to cancel — do NOT retry.
//  - retryable (429/5xx/network): transient — the merchant can try again.
//  - validation/unknown: generic fallback.
const errorMessageFor = category => {
  if (category === 'unauthenticated')
    return 'Your session has expired. Please sign in again.';
  if (category === 'not_found') return 'No active subscription to cancel.';
  if (category === 'retryable')
    return 'We couldn’t reach the server. Please try again in a moment.';
  return 'Something went wrong. Please try again.';
};

const formatEndDate = value => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  // Normalize to the UTC calendar day (see TrialBanner for the rationale).
  const day = new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  );
  return format(day, 'EEE, MMM d');
};

// Per-variant copy for the confirm and success screens.
//  - `endLabel`: the pre-submit estimate (trial end / next charge date). Only
//    ever drives the confirm-screen prompt.
//  - `successEndLabel`: the BFF's authoritative effectiveEndsAt, or null. When
//    null (e.g. an idempotent no-op returns 200 with effectiveEndsAt: null),
//    the success copy must NOT restate a date the server withheld, so it drops
//    the "until {date}" clause entirely rather than falling back to the estimate.
const copyFor = (isTrialing, endLabel, successEndLabel) =>
  isTrialing
    ? {
        title: 'Cancel your free trial?',
        prompt: `Your Profit Assist trial will end${
          endLabel ? ` on ${endLabel}` : ''
        }. You won't be charged, and you'll lose access to insights that help you find missed revenue and costly errors.`,
        confirmLabel: 'Cancel free trial',
        successTitle: 'Free trial canceled',
        successBody: successEndLabel
          ? `Your free trial has been canceled, but you can keep using Profit Assist until ${successEndLabel}. After that, your access will end, and you won't be charged.`
          : `Your free trial has been canceled. You won't be charged, and your access will end when the trial period is over.`,
      }
    : {
        title: 'Cancel your subscription?',
        prompt: `Your Profit Assist subscription will end${
          endLabel ? ` on ${endLabel}` : ''
        }. You'll lose access to insights that help you find missed revenue and costly errors.`,
        confirmLabel: 'Cancel subscription',
        successTitle: 'Subscription canceled',
        successBody: successEndLabel
          ? `Your subscription has been canceled, but you can keep using Profit Assist until ${successEndLabel}. After that, your access will end, and you won't be charged.`
          : `Your subscription has been canceled. Your access will end at the end of your current billing period, and you won't be charged again.`,
      };

/**
 * Cancellation modal for the "Subscription & Billing" page (PROF-1191).
 *
 * Renders the confirm survey (multi-select reason checkboxes + an
 * always-visible optional "Tell us more" field), submits the selected reasons +
 * detail on confirm, then swaps to the success/"request received" state.
 * Confirm is enabled from the start (independent of the survey). Copy adapts to
 * the trial vs paid variant.
 */
const CancelSubscriptionModal = ({
  isOpen,
  onClose,
  onConfirm,
  isTrialing = false,
  endDate = null,
}) => {
  const [reasons, setReasons] = useState([]);
  const [detail, setDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);
  // Terminal (non-retryable) failure — currently only a 404 "nothing to cancel".
  // Disables the confirm button so repeated clicks can't re-POST a request the
  // server has already said is a no-op.
  const [isTerminal, setIsTerminal] = useState(false);
  // Monotonic id for the in-flight submission. Bumped on every reset so a
  // promise that settles after the modal was closed/reset can't write stale
  // isDone/error state or leak into a reopened modal.
  const submissionRef = useRef(0);
  // Access-ends timestamp returned by the BFF (effectiveEndsAt). Preferred over
  // the prop `endDate` on the success screen since it's the authoritative value.
  const [effectiveEndsAt, setEffectiveEndsAt] = useState(null);

  // The confirm screen (pre-submit) uses the prop `endDate` estimate; the
  // success screen uses ONLY the BFF's authoritative effectiveEndsAt. Keeping
  // these separate ensures an idempotent no-op (200 with effectiveEndsAt: null)
  // never restates a date the server withheld.
  const endLabel = useMemo(() => formatEndDate(endDate), [endDate]);
  const successEndLabel = useMemo(
    () => formatEndDate(effectiveEndsAt),
    [effectiveEndsAt]
  );
  const copy = useMemo(
    () => copyFor(isTrialing, endLabel, successEndLabel),
    [isTrialing, endLabel, successEndLabel]
  );

  const reset = useCallback(() => {
    // Invalidate any in-flight submission so its late resolution is ignored.
    submissionRef.current += 1;
    setReasons([]);
    setDetail('');
    setIsSubmitting(false);
    setIsDone(false);
    setError(null);
    setDetailError(null);
    setIsTerminal(false);
    setEffectiveEndsAt(null);
  }, []);

  // Multi-select: toggle a reason in/out of the selection (selection order is
  // preserved). Deselecting "Other" clears the detail-required prompt.
  const handleReasonToggle = useCallback(
    value => {
      const willSelect = !reasons.includes(value);
      setReasons(
        willSelect ? [...reasons, value] : reasons.filter(v => v !== value)
      );
      if (value === OTHER_REASON && !willSelect) setDetailError(null);
    },
    [reasons]
  );

  const handleDetailChange = useCallback(e => {
    setDetail(e.target.value);
    // Clear the required prompt as soon as the merchant starts typing.
    if (e.target.value.trim()) setDetailError(null);
  }, []);

  const handleClose = useCallback(
    e => {
      // Don't allow closing (Nevermind / Esc / close control) while a request is
      // in flight — it would reset state out from under the pending promise and
      // could let the merchant reopen and fire a second request.
      if (isSubmitting) return;
      reset();
      onClose?.(e);
    },
    [onClose, reset, isSubmitting]
  );

  const handleConfirm = useCallback(async () => {
    // A terminal failure (404) is a no-op; block re-submits entirely.
    if (isTerminal) return;
    // "Other" requires the free-text field; block submission and prompt if empty.
    if (reasons.includes(OTHER_REASON) && !detail.trim()) {
      setDetailError(DETAIL_REQUIRED_MESSAGE);
      return;
    }
    // Tag this submission so a late resolution after reset/close is discarded.
    submissionRef.current += 1;
    const submissionId = submissionRef.current;
    const isCurrent = () => submissionRef.current === submissionId;
    setIsSubmitting(true);
    setError(null);
    // PROF-1193: reasons are verbatim label strings in a unique array. The survey
    // is multi-select, so map each selected value to its label (in selection
    // order); a no-reason submit sends an empty array.
    const reasonLabels = reasons
      .map(value => LABEL_BY_VALUE[value])
      .filter(Boolean);
    try {
      const result = await onConfirm?.({ reasons: reasonLabels, detail });
      // Ignore a resolution that belongs to a reset/closed submission.
      if (!isCurrent()) return;
      // Surface the authoritative access-ends timestamp on the success screen.
      if (result?.effectiveEndsAt) setEffectiveEndsAt(result.effectiveEndsAt);
      setIsDone(true);
    } catch (err) {
      if (!isCurrent()) return;
      // A 404 (nothing to cancel) is terminal — mark it so confirm disables and
      // the merchant can't re-POST. Retryable/validation keep confirm enabled;
      // anything unmapped falls back to a generic message.
      if (err?.category === 'not_found') setIsTerminal(true);
      setError(errorMessageFor(err?.category));
    } finally {
      if (isCurrent()) setIsSubmitting(false);
    }
  }, [onConfirm, reasons, detail, isTerminal]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isDone ? copy.successTitle : copy.title}
    >
      {isDone ? (
        <>
          <ModalBody>
            <BodyText as="p">{copy.successBody}</BodyText>
          </ModalBody>
          <ModalFooter>
            <Button variant="filled" onClick={handleClose}>
              Got it
            </Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalBody>
            {/* Stack the body sections with vertical rhythm (Figma 20393:12113:
                prompt, survey prompt, checkbox group, and detail field are
                spaced apart rather than butted together). */}
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <BodyText as="p">{copy.prompt}</BodyText>
              <LabelText as="p" size="large">
                {SURVEY_PROMPT}
              </LabelText>
              <div
                role="group"
                aria-label="Reason for canceling"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {CANCEL_REASONS.map(({ value, label }) => (
                  <Checkbox
                    key={value}
                    label={label}
                    checked={reasons.includes(value)}
                    onChange={() => handleReasonToggle(value)}
                  />
                ))}
              </div>
              <TextArea
                label="Tell us more"
                placeholder={
                  reasons.includes(OTHER_REASON) ? 'Required' : 'Optional'
                }
                value={detail}
                onChange={handleDetailChange}
                errorMessage={detailError || undefined}
              />
              {error && (
                <BodyText as="p" size="small">
                  {error}
                </BodyText>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="text"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Nevermind
            </Button>
            <Button
              variant="filled"
              destructive
              isLoading={isSubmitting}
              isDisabled={isTerminal}
              onClick={handleConfirm}
            >
              {copy.confirmLabel}
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default CancelSubscriptionModal;
